import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src";
import { wacToken } from "@/libraries/design/foundations";
import {
    Row,
    StyledBackground,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

export const IntegrationsSection = () => {
    const intl = useIntl();

    const header = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.IntegrationsSection.Header",
        defaultMessage: "Plug into your favorite tools"
    });
    const body = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.IntegrationsSection.Description",
        defaultMessage:
            "If your team uses it, we integrate with it. Choose from the over 3000+ integrations available in the Atlassian Marketplace."
    });
    const ctaText = intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.IntegrationsSection.Cta",
        defaultMessage: "Explore Integrations"
    });
    intl.formatMessage({
        id: "JiraSoftwareFeaturesPageV2.IntegrationsSection.ImageAlt",
        defaultMessage:
            "A collection of logos of tools that Jira integrates with"
    });

    const companyLogos1 = [
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/google-drive-icon.svg",
            alt: "Google Drive"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/google-docs-icon.svg",
            alt: "Google Docs"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/figma-icon.svg",
            alt: "Figma"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/slack-icon.svg",
            alt: "Slack"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/zoom-icon.svg",
            alt: "Zoom"
        }
    ];

    const companyLogos2 = [
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/teams-icon.svg",
            alt: "Microsoft Teams"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/salesforce-icon.svg",
            alt: "Salesforce"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/qmetry-icon.svg",
            alt: "QMetry"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/miro-icon.svg",
            alt: "Miro"
        },
        {
            src: "https://wac-cdn.atlassian.com/misc-assets/svgs/github-icon.svg",
            alt: "GitHub"
        }
    ];

    return (
        <TypographyStyles>
            <div
                css={css({
                    "backgroundColor": wacToken("color.neutral.light100"),
                    "clipPath": "polygon(0 5%, 100% 0, 100% 100%, 0 100%)",
                    "padding": "64px 0",
                    // Tech debt: Adding this disable to unblock commits for GWP-2646

                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ".styled-background": {
                        [media.above.xl]: {
                            backgroundPosition:
                                "calc(50% + 575px), calc(50% - 575px)",
                            backgroundImage:
                                "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/XL-Logo-Right-3120803437.webp), url(https://wac-cdn.atlassian.com/misc-assets/webp-images/XL-Logo-Left-801937110.webp)"
                        }
                    },

                    [media.below.sm]: {
                        padding: "64px 0 0 0",
                        clipPath: "none"
                    }
                })}
            >
                <StyledBackground
                    bgColor={wacToken("color.neutral.light100")}
                    partialBackgrounds={[
                        {
                            imageLink:
                                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Logo_wall_left.svg",
                            positionDesktop: "sideLeft",
                            positionTablet: "hidden",
                            positionMobile: "hidden"
                        },
                        {
                            imageLink:
                                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Logo_wall_right.svg",
                            positionDesktop: "sideRight",
                            positionTablet: "hidden",
                            positionMobile: "hidden"
                        }
                    ]}
                >
                    <Row layout="12">
                        <section
                            css={css({
                                margin: "64px 0",
                                display: "flex",
                                flexDirection: "column",
                                gap: "32px",

                                [media.below.sm]: {
                                    marginBlockStart: "0"
                                }
                            })}
                        >
                            <div
                                css={css({
                                    "display": "flex",
                                    "flexDirection": "column",
                                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                    "h2, p, a": {
                                        textAlign: "center",
                                        margin: "0 auto"
                                    },
                                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                    "p": {
                                        margin: "8px auto 16px",
                                        maxWidth: "600px"
                                    }
                                })}
                            >
                                <h2>{header}</h2>
                                <p>{body}</p>
                                <Adg4Button
                                    variant="secondary"
                                    color="black"
                                    href="https://marketplace.atlassian.com/"
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container":
                                            "integrationsSection",
                                        "data-product-key": "jira",
                                        "data-label": ctaText,
                                        "data-label-english":
                                            "Explore Integrations"
                                    }}
                                >
                                    {ctaText}
                                </Adg4Button>
                            </div>
                            <div
                                css={css({
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    maxWidth: "400px",
                                    gap: "25px",
                                    margin: "auto",

                                    [media.above.sm]: {
                                        maxWidth: "100%",
                                        display: "grid",
                                        gridTemplateColumns: "repeat(5, 1fr)",
                                        justifyItems: "center"
                                    }
                                })}
                            >
                                {companyLogos1.map((logo, index) => (
                                    <CompanyLogo
                                        key={index}
                                        src={logo.src}
                                        alt={logo.alt}
                                        visibleBreakpoint="onlyTablet"
                                    />
                                ))}
                                {companyLogos2.map((logo, index) => (
                                    <CompanyLogo
                                        key={index}
                                        src={logo.src}
                                        alt={logo.alt}
                                        visibleBreakpoint="mobileAndTablet"
                                    />
                                ))}
                            </div>
                        </section>
                    </Row>
                </StyledBackground>
            </div>
        </TypographyStyles>
    );
};

interface CompanyLogoProps {
    src: string;
    alt: string;
    visibleBreakpoint: "mobileAndTablet" | "onlyTablet";
}

const CompanyLogo = ({ src, alt, visibleBreakpoint }: CompanyLogoProps) => {
    return (
        <div
            css={[
                css({
                    display: "none",
                    borderRadius: "30px",
                    alignItems: "center",
                    justifyContent: "center",
                    background: wacToken("color.neutral.light0"),
                    width: "100px",
                    height: "100px",
                    padding: "20px",
                    // Tech debt: Adding this disable to unblock commits for GWP-2646
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    img: {
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto"
                    }
                }),
                visibleBreakpoint === "onlyTablet" &&
                    css({
                        [media.only.sm]: { display: "flex" },
                        [media.only.md]: { display: "flex" }
                    }),
                visibleBreakpoint === "mobileAndTablet" &&
                    css({ [media.below.lg]: { display: "flex" } })
            ]}
        >
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img src={src} alt={alt} loading="lazy" />
        </div>
    );
};
